import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { StrategySimulationContext } from '../..';
import tittleDecoration from '../../imgs/tittleDecoration.png';
import styles from './index.module.scss';
import { DatePicker, Empty, Radio, EchartsWithTheme, useTheme } from '@maxtropy/components';
import { ElecType, ElecTypeDisplay, ResolutionEnum, ResolutionEnumDisplay } from '../../type';
import dayjs, { Dayjs } from 'dayjs';
import EachCard from '../EachCard';
import iconYesterday from '../../imgs/icon-yesterday.png';
import iconLastmonth from '../../imgs/icon-lastmonth.png';
import iconThismonth from '../../imgs/icon-thismonth.png';
import { Spin } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2MicrogridResolvedGetAnalysisReportPost,
  apiV2MicrogridResolvedGetAnalysisReportTypePost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import { getChartOption } from './chart';
import { isNil } from 'lodash-es';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';

const Forecasting = () => {
  const { strategyId, lastStartTime } = useContext(StrategySimulationContext);

  const [resolution, setResolution] = useState<ResolutionEnum>(ResolutionEnum.DAY);
  const [type, seType] = useState<ElecType>();
  const [date, setDate] = useState<Dayjs>(dayjs());

  const theme = useTheme();

  // 默认选择求解开始时间 （先废弃）
  // useEffect(() => {
  //   if (lastStartTime) {
  //     setDate(dayjs(lastStartTime));
  //   }
  // }, [lastStartTime]);

  // 预测分析报表
  const { data, loading } = useRequest(
    () =>
      apiV2MicrogridResolvedGetAnalysisReportPost({
        strategyId,
        startTime: resolution === ResolutionEnum.DAY ? date.startOf('day').valueOf() : date.startOf('month').valueOf(),
        endTime: resolution === ResolutionEnum.DAY ? date.endOf('day').valueOf() : date.endOf('month').valueOf(),
        timeResolution: resolution,
        // @ts-ignore
        predictType: type,
      }).then(res => {
        return res;
      }),
    {
      ready: !!strategyId && !!date && !isNil(type),
      refreshDeps: [strategyId, date, type],
    }
  );

  // 获取预测分析支持的类型
  const { data: typeList } = useRequest(
    () =>
      apiV2MicrogridResolvedGetAnalysisReportTypePost({
        id: strategyId,
      }).then(res => {
        return res?.list;
      }),
    {
      ready: !!strategyId,
      refreshDeps: [strategyId],
    }
  );

  // 默认选择第一个类型
  useEffect(() => {
    if (!typeList || typeList.length === 0) return;
    seType(typeList?.[0].type);
  }, [typeList]);

  // 图表
  const chartOption = useMemo(() => getChartOption(resolution, data), [data, resolution]);

  const chartRef = useRef<EchartsWithThemeRef>();
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.getInstance()?.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
        <span className={styles.title}>预测分析</span>
      </div>
      <div className={styles.content_box}>
        <div className={styles.select_box}>
          <Radio.Group
            buttonStyle="solid"
            value={resolution}
            onChange={e => {
              setResolution(e.target.value);
              // setDate(lastStartTime ? dayjs(lastStartTime) : dayjs());
              setDate(dayjs());
            }}
          >
            <Radio.Button value={ResolutionEnum.DAY}>{ResolutionEnumDisplay[ResolutionEnum.DAY]}</Radio.Button>
            <Radio.Button value={ResolutionEnum.MONTH}>{ResolutionEnumDisplay[ResolutionEnum.MONTH]}</Radio.Button>
          </Radio.Group>
          <DatePicker
            style={{ width: 240 }}
            allowClear={false}
            picker={resolution === ResolutionEnum.DAY ? 'date' : 'month'}
            value={date}
            onChange={date => setDate(date)}
            disabledDate={current => {
              // 只允许选择 startDate ~ endDate 范围内的日期
              if (lastStartTime) {
                return (
                  current < dayjs(lastStartTime).startOf(resolution === ResolutionEnum.DAY ? 'days' : 'month') ||
                  current > dayjs().add(1, resolution === ResolutionEnum.DAY ? 'days' : 'month')
                );
              }
              return current > dayjs().add(1, 'days');
            }}
          />

          <Radio.Group
            buttonStyle="solid"
            value={type}
            onChange={e => {
              seType(e.target.value);
            }}
          >
            {(typeList ?? []).map(item => (
              <Radio.Button key={item.type} value={item.type}>
                {item.name}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
        <div className={styles.card_box}>
          {resolution === ResolutionEnum.DAY ? (
            <EachCard
              img={iconYesterday}
              lineOne={{
                label: `实际${!isNil(type) ? ElecTypeDisplay[type] : ''}`,
                value: data?.lastValue,
                unit: 'kWh',
              }}
              lineTwo={{
                label: `算法预测${!isNil(type) ? ElecTypeDisplay[type] : ''}`,
                value: data?.lastPredictValue,
                unit: 'kWh',
              }}
            />
          ) : (
            <>
              <EachCard
                img={iconLastmonth}
                lineOne={{
                  label: `实际${!isNil(type) ? ElecTypeDisplay[type] : ''}`,
                  value: data?.lastValue,
                  unit: 'kWh',
                }}
                lineTwo={{
                  label: `算法预测${!isNil(type) ? ElecTypeDisplay[type] : ''}`,
                  value: data?.lastPredictValue,
                  unit: 'kWh',
                }}
              />
              <EachCard
                img={iconThismonth}
                lineOne={{
                  label: `实际${!isNil(type) ? ElecTypeDisplay[type] : ''}`,
                  value: data?.currentValue,
                  unit: 'kWh',
                }}
                lineTwo={{
                  label: `算法预测${!isNil(type) ? ElecTypeDisplay[type] : ''}`,
                  value: data?.currentPredictValue,
                  unit: 'kWh',
                }}
              />
            </>
          )}
        </div>
        <div className={styles.chart_box}>
          <EchartsWithTheme
            empty={{
              isShowEmpty: !data?.actValues?.length && !data?.predictValues?.length,
              emptyNode: <Empty />,
            }}
            loading={loading}
            theme={theme}
            ref={d => {
              if (d) {
                chartRef.current = d;
              }
            }}
            style={{ height: 340, width: '100%' }}
            option={chartOption}
          />
        </div>
      </div>
    </div>
  );
};

export default Forecasting;
